import { useState, useRef, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { Volume2, VolumeX } from 'lucide-react';

const PowerCardWithAudio = ({ question, answer, audioId, index }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);
  const ref = useRef(null);
  const questionAudioRef = useRef(null);
  const answerAudioRef = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  useEffect(() => {
    questionAudioRef.current = new Audio(`/audio/${audioId}-question.mp3`);
    answerAudioRef.current = new Audio(`/audio/${audioId}-answer.mp3`);
    
    questionAudioRef.current.addEventListener('ended', () => {
      setTimeout(() => {
        answerAudioRef.current?.play().catch(() => {});
      }, 300);
    });
    
    answerAudioRef.current.addEventListener('ended', () => {
      setIsPlaying(false);
    });

    return () => {
      questionAudioRef.current?.pause();
      answerAudioRef.current?.pause();
    };
  }, [audioId]);

  const playAudio = () => {
    if (isPlaying) {
      questionAudioRef.current?.pause();
      answerAudioRef.current?.pause();
      setIsPlaying(false);
    } else {
      setShowAnswer(true);
      questionAudioRef.current.currentTime = 0;
      answerAudioRef.current.currentTime = 0;
      questionAudioRef.current?.play().catch(() => {});
      setIsPlaying(true);
    }
  };

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.15 }}
      whileHover={{ 
        scale: 1.02,
        y: -5,
        boxShadow: '0 10px 40px rgba(59, 130, 246, 0.15)'
      }}
      className="bg-[#1a1a2e] border border-white/10 rounded-2xl p-6 transition-colors group cursor-pointer relative"
      onClick={playAudio}
    >
      {/* Play indicator */}
      <motion.button
        className="absolute top-4 right-4 p-2 rounded-full bg-[#09090b]/50 border border-white/10"
        animate={!isPlaying ? {
          scale: [1, 1.1, 1],
          opacity: [0.5, 1, 0.5]
        } : {}}
        transition={{ duration: 2, repeat: !isPlaying ? Infinity : 0 }}
      >
        {isPlaying ? (
          <Volume2 className="w-4 h-4 text-[#3b82f6]" />
        ) : (
          <VolumeX className="w-4 h-4 text-[#71717a]" />
        )}
      </motion.button>

      <p className="text-[#3b82f6] font-medium mb-3 group-hover:text-[#60a5fa] transition-colors pr-10">
        "{question}"
      </p>
      
      <motion.p 
        className="text-[#a1a1aa] text-sm leading-relaxed"
        initial={{ opacity: 0.7 }}
        animate={{ opacity: showAnswer || isPlaying ? 1 : 0.7 }}
      >
        {answer}
      </motion.p>

      {/* Audio wave animation when playing */}
      {isPlaying && (
        <div className="absolute bottom-4 right-4 flex items-center gap-1">
          {[...Array(4)].map((_, i) => (
            <motion.div
              key={i}
              className="w-1 bg-[#3b82f6] rounded-full"
              animate={{ height: [8, 16, 8] }}
              transition={{ 
                duration: 0.5, 
                repeat: Infinity, 
                delay: i * 0.1,
                ease: "easeInOut"
              }}
            />
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default PowerCardWithAudio;
